import ConnectableObject from "./ConnectableObject";
import Point from "./Point";
import BaseObject from "./BaseObject";
import { copyAllProperties } from "./utils";
import BaseModel from "./BaseModel";
import type Page from "./Page";
import type Device from "./Device";
import Consumer from "./Consumer";
import { PortInfo } from "./PortInfos";
import { KnxSceneInfo } from "./KnxSceneInfo";
import Logger from "../Logger";
import Channel from "./Channel";
import type Actor from "./Actor";
import type Scene from "./Scene";
import type Room from "./Room";
import { EventKey } from "../eventLogger/EventKeys";
import type ButtonChannel from "./ButtonChannel";
import { itx, noItx } from "../translationItx/itx";
import { estadaConfirm } from "../modelDialogs/ConfirmDialog";
const logger = new Logger();

////////////////////////////////////////////////////

export enum ConnectionType {
  Unknown = "Unknown", // no itx
  Channel = "Channel", // no itx
  Scene = "Scene", // no itx
  Multiplexer = "Multiplexer", // no itx
  Button = "Button", // no itx
  FromSourceDevice = "FromSourceDevice", // no itx
  FromTargetDevice = "FromTargetDevice", // no itx
  TerminalVia = "TerminalVia", // no itx
}

export function getTypeFromDevice(device: ConnectableObject): ConnectionType {
  let res = ConnectionType.Unknown;
  switch (device.className) {
    case noItx("Multiplexer"): {
      res = ConnectionType.Multiplexer;
      break;
    }
    case noItx("Channel"): {
      res = ConnectionType.Channel;
      break;
    }
    case noItx("Scene"): {
      res = ConnectionType.Scene;
      break;
    }
    case noItx("Consumer"):
    case noItx("ButtonChannel"):
    case noItx("Button"): {
      // console.log("MWE todo feinschliff nötig");
      if (device.isActorChannel) {
        // ActorChannel in buttons
        res = ConnectionType.Channel;
      } else {
        res = ConnectionType.Button;
      }
      break;
    }
  }

  return res;
}

export function ConnectionTypeFromString(channelType: string): ConnectionType {
  let res = ConnectionType.Unknown;

  for (const i in ConnectionType) {
    if (i === channelType) {
      res = i as ConnectionType;
    }
  }

  return res;
}

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { ChannelType, channelTypeInfo, electricalChannelType } from "./ChannelType";
import { PagePrintInfo } from "./PagePrintInfo";
import Multiplexer from "./Multiplexer";
import AllowedConnections from "./AllowedConnections";
import { time } from "console";
import Button from "./Button";

ObjectFactory.registerClass("Connection", (data?: any, parent?: BaseModel) => {
  return new Connection(data, parent);
});
//////////////////////////////////////////////////

export default class Connection extends BaseModel {
  animateFromPoint?: Point;
  animateStartTime?: number; // startTime in milliseconds

  public get className(): string {
    return "Connection"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return "connections"; // no itx
  }

  // persistent properties

  // -----------------------------------------------------------------------------
  // type
  // -----------------------------------------------------------------------------
  _type = ConnectionType.Unknown;
  get type(): ConnectionType {
    return this._type;
  }

  set type(newValue: ConnectionType) {
    if (this._type === newValue) return;
    this.beforeChange("type");
    this._type = newValue;
    this.changed("type");
  }

  // -----------------------------------------------------------------------------
  // fromId
  // -----------------------------------------------------------------------------
  _fromId: string | undefined = undefined;
  get fromId(): string | undefined {
    return this._fromId;
  }

  set fromId(newValue: string | undefined) {
    if (this._fromId === newValue) return;
    this.beforeChange("fromId");
    this._fromId = newValue;
    this.changed("fromId");
  }

  // -----------------------------------------------------------------------------
  // fromPortId
  // -----------------------------------------------------------------------------
  _fromPortId: string | undefined = undefined;
  get fromPortId(): string | undefined {
    return this._fromPortId;
  }

  set fromPortId(newValue: string | undefined) {
    if (this._fromPortId === newValue) return;
    this.beforeChange("fromPortId");
    this._fromPortId = newValue;
    this.changed("fromPortId");
  }

  // -----------------------------------------------------------------------------
  // toId
  // -----------------------------------------------------------------------------
  _toId: string | undefined = undefined;
  get toId(): string | undefined {
    return this._toId;
  }

  set toId(newValue: string | undefined) {
    if (this._toId === newValue) return;
    this.beforeChange("toId");
    this._toId = newValue;
    this.changed("toId");
  }

  // -----------------------------------------------------------------------------
  // toPortId
  // -----------------------------------------------------------------------------
  _toPortId: string | undefined = undefined;
  get toPortId(): string | undefined {
    return this._toPortId;
  }

  set toPortId(newValue: string | undefined) {
    if (this._toPortId === newValue) return;
    this.beforeChange("toPortId");
    this._toPortId = newValue;
    this.changed("toPortId");
  }

  /////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // knxSceneInfo
  // -----------------------------------------------------------------------------
  _knxSceneInfo: KnxSceneInfo | undefined;

  get knxSceneInfo(): KnxSceneInfo | undefined {
    return this._knxSceneInfo;
  }
  set knxSceneInfo(newValue: KnxSceneInfo | undefined) {
    this.beforeChange("knxSceneInfo");
    this._knxSceneInfo = newValue;
    if (newValue) {
      newValue.parent = this;
    }

    this.changed("knxSceneInfo");
  }

  // -----------------------------------------------------------------------------
  //////////////////////////////////////////////////////////////////////////

  //only for copy
  sourceFromId?: string;
  sourceToId?: string;
  executePasteDone?: boolean;

  //only for sorting

  // -----------------------------------------------------------------------------
  // scenePartnerTagOrName
  // -----------------------------------------------------------------------------
  _scenePartnerTagOrName = this.scenePartnerTagOrName;
  get scenePartnerTagOrName(): string | undefined {
    if (this.fromDevice?.className == "Scene") {
      return this.toDevice?.tagOrName;
    } else if (this.toDevice?.className == "Scene") {
      return this.fromDevice?.tagOrName;
    }
    return undefined;
  }

  // -----------------------------------------------------------------------------

  private _fromPort: PortInfo | undefined = undefined;

  fromPort(document: Document, mainPageId: string): PortInfo | undefined {
    if (true || this._fromPort == undefined) {
      this._fromPort = this.fromDevice?.getPortPosition(document, mainPageId, this);
    }

    if (!this._fromPort) {
      logger.log(`get fromPort failed: ${this.fromId} ${this.fromDevice?.tag}`);
    }

    return this._fromPort;
  }

  _toPort: PortInfo | undefined = undefined;
  toPort(document: Document, mainPageId: string): PortInfo | undefined {
    if (true || this._toPort == undefined) {
      this._toPort = this.toDevice?.getPortPosition(document, mainPageId, this);
    }

    //
    // while we are drawing a connection e dont yet have a "toDevice"
    // insteed the "tmpToPoint" property is set
    //
    if (this._toPort == undefined && this.tmpToPoint) {
      return new PortInfo(this.tmpToPoint, undefined);
    }

    return this._toPort;
  }

  innerPoints = new Array<Point>();

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    copyAllProperties(data, this);
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    if (this.fromDevice) {
      this.fromDevice.onDisconnect(this);
    }

    if (this.toDevice) {
      this.toDevice.onDisconnect(this);
    }

    this.removeFromConnectableObjects();
    if (state.selectionManager.selectedObject == this) {
      state.selectionManager.clearSelectionPreservingAnalysis();
    }

    // Achtung
    // ERST die Connection löschen
    // DANN etwaige nicht mehr benötigten automatisch erstellen Channels und Aktoren
    //
    // DENN
    //
    // Beim UNDO werden die Objekte in umgekehrter Reihenfolge wieder hergestellt.
    // Das heißt, beim Undo der Connection muß der Channel bereits wiederhergestellt sein.
    //
    let res = super.delete();

    res = res.concat(this.removeChannelIfNeeded());

    this.updateConnectedPartners();
    return res;
  }

  undelete() {
    super.undelete();

    this.addToConnectableObjects();
    this.updateConnectedPartners();
  }

  /////////////////////////////////////////////////////////

  getPersistentProperties(): string[] {
    return [
      "type", // no itx

      "toId", // no itx
      "toPortId", // no itx
      "toPoint", // no itx

      "fromId", // no itx
      "fromPortId", // no itx
      "fromPoint", // no itx

      "oref:knxSceneInfo", // no itx

      ...super.getPersistentProperties(),
    ];
  }

  fromJSON(data: any, parent: BaseObject | undefined = undefined): void {
    super.fromJSON(data, parent);
    logger.log("Connection fromJSON done");
  }

  async loadFromEstadaStorage(
    data: any | undefined,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> {
    logger.log("Connection.loadFromEstadaStorage");

    const res = await super.loadFromEstadaStorage(data, parent, loadData);
    return res;
  }

  ////////////////////////////////////////////////////////

  setSelected(newValue: boolean) {
    super.setSelected(newValue);
  }

  tmpType: ConnectionType | undefined = undefined;
  tmpToPoint: Point | undefined;
  tmpFromPoint: Point | undefined;

  mouseIsDown = false;

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return (this.parent as Page)?.connections.asArray();
  }

  //////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // isHighlighted
  // -----------------------------------------------------------------------------
  _isHighlighted: boolean = false;
  get isHighlighted(): boolean {
    return this._isHighlighted || this.isNew || false;
  }
  set isHighlighted(newValue: boolean) {
    this._isHighlighted = newValue;
  }

  // -----------------------------------------------------------------------------

  setIsHighlighted(newValue: boolean) {
    const lastIsHighlighted = this.isHighlighted;
    this.isHighlighted = newValue;

    if (lastIsHighlighted != this.isHighlighted) {
      this.raiseOnChange("isHighlighted");
    }
  }

  //////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // isHovered
  // -----------------------------------------------------------------------------
  _isHovered: boolean = false;
  get isHovered(): boolean {
    return this._isHovered || this.isNew || false;
  }
  set isHovered(newValue: boolean) {
    this._isHovered = newValue;
  }

  // -----------------------------------------------------------------------------

  setIsHovered(newValue: boolean) {
    const lastIsHoverd = this.isHovered;
    this.isHovered = newValue;

    if (lastIsHoverd != this.isHovered) {
      this.raiseOnChange("isHovered");
    }
  }

  //////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // forbidden
  // -----------------------------------------------------------------------------
  _forbidden = false;
  // for debugging purposed only
  set forbidden(newValue: boolean) {
    if (this._forbidden != newValue) {
      this._forbidden = newValue;
    }

    //
    // MWE 30.10.2025
    // das liefert wir in "get errors" in ConnecableObject nun dynamisch zurück
    //

    // if (!this.tmpToPoint) {
    //   //
    //   // tmpToPoint is set while a new connection is drawn
    //   // for these temporary connections we set the forbidden flag during mouseMoves
    //   // but we dont want to flag any errors to connected devices
    //   //
    //   if (this.fromDevice) {
    //     this.fromDevice.flagErrors({ invalidConnection: "" }, newValue);
    //   }
    //   if (this.toDevice) {
    //     this.toDevice.flagErrors({ invalidConnection: "" }, newValue);
    //   }
    // }
  }

  get forbidden(): boolean {
    return this._forbidden;
  }

  // -----------------------------------------------------------------------------

  checkForbidden(): boolean {
    if (this.fromDevice && this.toDevice) {
      this.forbidden = !AllowedConnections.isConnectionAllowed(this.fromDevice, this.toDevice, false);
    } else {
      this._forbidden = true;
    }

    this.flagErrors({ invalidConnection: "" }, this._forbidden);

    return this._forbidden;
  }

  get fromDevice(): ConnectableObject | undefined {
    return this.state.objectRegistry.getObject(this.fromId);
  }
  set fromDevice(device: ConnectableObject | undefined) {
    if (device) {
      this.fromId = device.id;
    } else {
      this.fromId = undefined;
    }
    this._fromPort = undefined;
  }

  get toDevice(): ConnectableObject | undefined {
    return this.state.objectRegistry.getObject(this.toId);
  }

  set toDevice(device: ConnectableObject | undefined) {
    if (device) {
      this.toId = device.id;
    } else {
      this.toId = undefined;
    }
    this._toPort = undefined;
  }

  get color() {
    let type = this.type;

    if (type == ConnectionType.Unknown && this.tmpType) {
      type = this.tmpType;
    }
    // ButtonChannel Type error(mismatched RequieredChannel/knxChannel)
    if (
      (this.fromDevice && this.checkTypeErrorOfChannel(this.fromDevice)) ||
      (this.toDevice && this.checkTypeErrorOfChannel(this.toDevice))
    ) {
      return "var(--connectionColorForbidden)"; // Red  // no itx
    }

    if (type == ConnectionType.FromSourceDevice || type == ConnectionType.FromTargetDevice) {
      if (this.fromDevice) {
        type = getTypeFromDevice(this.fromDevice);
      } else {
        return "var(--connectionColorForbidden)"; // Red  // no itx
      }
    }

    if (this.selected) {
      return "var(--connectionColorHighlight)"; // Orange  // no itx
    }

    if (this.forbidden) {
      logger.log("connection forbidden -> red", this);
      return "var(--connectionColorForbidden)"; // Red  // no itx
    }
    //logger.log("color= " + type);

    switch (type) {
      case ConnectionType.Unknown:
        if (this.toDevice) {
          logger.log("connection unknown -> red(forbidden)", this);
          return "var(--connectionColorForbidden)"; // Red  // no itx
        } else {
          logger.log("connection unknown -> darkgrey(unknown)", this);
          return "var(--connectionColorUnknown)"; // no itx
        }
      case ConnectionType.Channel:
        return "var(--connectionColorChannel)"; // Green  // no itx
      case ConnectionType.Button:
      case ConnectionType.Multiplexer:
        return "var(--connectionColorButton)"; // Blue  // no itx
      case ConnectionType.Scene:
        return "var(--connectionColorScene)"; // Yellow  // no itx
      case ConnectionType.TerminalVia:
        return "brown"; // no itx
    }

    return "var(--knxColorRed)"; // Red  // no itx
  }
  ////////////////////////////////////////////////////////
  checkTypeErrorOfChannel(channel: ConnectableObject): boolean {
    const haschannelTypeError = channel.errors?.channelTypeError != undefined && channel.errors?.channelTypeError != "";
    return channel.isChannel && haschannelTypeError;
  }
  ////////////////////////////////////////////////////////

  updateEndPoints() {
    //logger.log(`updateEndPoints connection=${this.id}`);
    // clear cached PortInfos
    this._fromPort = undefined;
    this._toPort = undefined;
    this.raiseOnChange("endPoints");

    // if (this.parent) {
    //   const pageId = this.parent.id;

    //   var changed = false;

    //   if (this.fromDevice) {
    //     const x = this.fromPort?.point?.x;
    //     const y = this.fromPort?.point?.y;

    //     this._fromPort = undefined

    //     const p = this.fromDevice.getPortPosition(pageId, this.fromPortId);
    //     if (p) {
    //       this.fromPort = p;
    //     }
    //     changed = x != this.fromPort?.point?.x || y != this.fromPort?.point?.y;
    //   }

    //   if (this.toDevice) {
    //     const x = this.toPort?.point?.x;
    //     const y = this.toPort?.point?.y;
    //     const p = this.toDevice.getPortPosition(pageId, this.fromPortId);
    //     if (p) {
    //       this.toPort = p;
    //     }
    //     changed =
    //       changed || x != this.toPort?.point?.x || y != this.toPort?.point?.y;
    //   }

    //   if (changed) {
    //     this.raiseOnChange("endPoints");
    //   }
    // }
  }

  async updateConnectedPartners() {
    this.toDevice?.raiseOnChange("connection");
    this.fromDevice?.raiseOnChange("connection");

    // this is now done in "onConnect" of a channel
    //
    // const dFrom = this.fromDevice?.parent as Device;
    // if (dFrom && dFrom.getIsDevice && dFrom.getIsDevice()) {
    //   await dFrom?.updateAllFromKnxMetadata();
    //   dFrom.knxInstanceData?.raiseOnChange();
    // }
    // //
    // const dTo = this.toDevice?.parent as Device;
    // if (dTo && dTo.getIsDevice && dTo.getIsDevice()) {
    //   await dTo?.updateAllFromKnxMetadata();
    //   dTo.knxInstanceData?.raiseOnChange();
    // }

    // const scene = this.getScene();
    // if (scene) {
    //   const partner = this.getPartner(scene);
    //   if (partner?.isConsumer) {
    //     const consumer = partner as Consumer;
    //     const channel = consumer.getConnectedChannel();
    //     const actor = channel?.parent as Actor;
    //     await actor?.updateAllFromKnxMetadata();
    //     actor?.raiseOnChange();
    //     actor?.knxInstanceData?.raiseOnChange();
    //   }
    // }
  }

  getPartner(c: ConnectableObject): ConnectableObject | undefined {
    if (this.fromDevice === c) {
      return this.toDevice;
    } else if (this.toDevice === c) {
      return this.fromDevice;
    } else {
      return undefined;
    }
  }

  removeFromPage() {
    const page = this.parent as Page;
    if (page) {
      page.connections.remove(this);
    }
  }

  removeChannelIfNeeded(): Array<BaseObject> {
    let res = new Array<BaseObject>();

    // deleteChannel If User didn`t created the number of Channels

    let actorChannelPartner: Channel | undefined = undefined;
    let deleteChannel = false;

    // get connected Channel
    if (this.fromDevice?.className == "Channel") {
      actorChannelPartner = this.fromDevice as Channel;
    } else if (this.toDevice?.className == "Channel") {
      actorChannelPartner = this.toDevice as Channel;
    }

    if (actorChannelPartner) {
      if (actorChannelPartner.parent?.className == "Actor") {
        const actor = actorChannelPartner.parent as Actor;
        // only delete Channel if given Number from User is smaller than actor.channels.size
        // if User didn`t edited the number of Channels
        // and if no actor don`t has a Template
        if (!actor.template) {
          if (actor.numberChannelsFromUser && actor.numberChannelsFromUser < actor.channels.size) {
            deleteChannel = true;
          } else if (actor.numberChannelsFromUser == undefined) {
            deleteChannel = true;
          }
        } else {
          if (!actorChannelPartner.hasErrors && !actorChannelPartner.mappedChannel) {
            // delete Channel (after delete of connection) if channel is overstayed because of the connection after mapping
            deleteChannel = true;
          }
        }

        if (deleteChannel) {
          res = res.concat(actorChannelPartner.delete());
          //delete Actor, if no Channels are in Actor
          //and if User didn`t edited the number of Channels
          if (actor.numberChannelsFromUser == undefined && actor.channels.size == 0) {
            res = res.concat(actor.delete());
          }
        }
      }
    } else {
      let buttonChannelPartner: ButtonChannel | undefined = undefined;
      // get connected Channel
      if (this.fromDevice?.isButtonChannel) {
        buttonChannelPartner = this.fromDevice as ButtonChannel;
      } else if (this.toDevice?.isButtonChannel) {
        buttonChannelPartner = this.toDevice as ButtonChannel;
      }

      if (buttonChannelPartner) {
        //
        // MWE 10.3.2026
        // dont delete ButtonChannels of Buttons with a template !!!!
        //
        const button = buttonChannelPartner.parent as Button;
        if (!button.template) {
          if (
            buttonChannelPartner.connections.size == 0 &&
            (this.parent as Device).template == undefined &&
            buttonChannelPartner.canBeDeletedInteractive()
          ) {
            res = res.concat(buttonChannelPartner.delete());
          }
        }
      }
    }

    return res;
  }

  addToPage(newPage?: Page) {
    const page = newPage || (this.parent as Page);
    if (page) {
      this.parent = page;
      page.connections.add(this);
    }
  }

  handleFromConnectableObjectsForOneDevice(device: ConnectableObject | undefined) {
    if (device) {
      device.connections.remove(this);
      device?.raiseOnChange("connections");
      if (device.parent) {
        if (device.parent.className == "Actor") {
          if (this.errors?.invalidConnection != undefined) {
            //
            // Remove invalidConnection-Error from connected Partner
            // when deleting a connection
            //
            device.flagErrors({ invalidConnection: "" }, false);
          }
          device.parent.raiseOnChange("connections");
        }
      }
    }
  }
  removeFromConnectableObjects() {
    this.handleFromConnectableObjectsForOneDevice(this.toDevice);
    this.handleFromConnectableObjectsForOneDevice(this.fromDevice);

    this.fromDevice?.onDisconnect(this);
    this.toDevice?.onDisconnect(this);
  }

  removeFromConnectableObjectsWithoutUpdate() {
    this.toDevice?.connections.removeWithoutUpdate(this);
    this.fromDevice?.connections.removeWithoutUpdate(this);
  }

  addToConnectableObjects() {
    this.addToConnectableObjectsWithoutUpdate();

    this.toDevice?.raiseOnChange("connections");
    this.fromDevice?.raiseOnChange("connections");

    this.fromDevice?.onConnect(this);
    this.toDevice?.onConnect(this);
  }

  addToConnectableObjectsWithoutUpdate() {
    const fd = this.toDevice;
    const td = this.fromDevice;
    // logger.log(
    //   `connection addWithoutUpdate${this.id} ${this.type} fromDevice=${fd?.name}  toDevice=${td?.name}`,
    // );

    if (fd && td) {
      fd.connections.addWithoutUpdate(this);
      td.connections.addWithoutUpdate(this);
    }
  }
  specifyGenericConnection() {
    if (this.fromDevice && this.toDevice) {
      if (this.fromDevice.channelType == ChannelType.Generic) {
        this.fromDevice.channelType = this.toDevice.channelType;
        this.fromDevice.raiseOnChange("channelType");
      }

      if (this.toDevice.channelType == ChannelType.Generic) {
        this.toDevice.channelType = this.fromDevice.channelType;
        this.toDevice.raiseOnChange("channelType");
      }
    }
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    if (this.fromDevice && this.toDevice) {
      res.push(this.fromDevice);
      res.push(this.toDevice);
    }
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      if (this.sourceFromId || this.sourceToId) {
        // connection mapped twice
        // this is not an error !!!
      } else {
        this.sourceFromId = this.fromId;
        this.sourceToId = this.toId;
        this.fromId = state.objectMapping.getMappedId(this.fromId);
        this.toId = state.objectMapping.getMappedId(this.toId);
      }
    }
  }
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return (await this.parent?.pasteItem(params)) || [];
  }

  executePasteHelper(params: {
    isCopyRootObj: boolean;
    initiator: ConnectableObject;
    undoObjects: Array<BaseModel>;
    page: Page;
  }) {
    const { isCopyRootObj, initiator, undoObjects, page } = params;

    if (!this.executePasteDone) {
      this.executePasteDone = true;

      if (this.type == "Button" || this.type == "Scene") {
        const copyObj = this.getPartner(initiator);
        if (copyObj) {
          undoObjects.push(this);

          if (isCopyRootObj || this.type == "Scene") {
            const obj = this.state.objectRegistry.getObject(copyObj.sourceId) as ConnectableObject;
            if (obj) {
              this.removeFromConnectableObjectsWithoutUpdate();
              this.replaceDevice(copyObj, obj);
              this.addToConnectableObjectsWithoutUpdate();
              this.addToPage(page);
            }
          }
        } else {
          this.delete();
        }
      } else {
        if (!globalThis.compareState) {
          this.delete();
        }
      }
    }
  }

  replaceDevice(oldObject: ConnectableObject, newObject: ConnectableObject) {
    if (this.fromId == oldObject.id) {
      this.fromId = newObject.id;
    }
    if (this.toId == oldObject.id) {
      this.toId = newObject.id;
    }
  }

  getAny(className: string): ConnectableObject | undefined {
    let scene: ConnectableObject | undefined;

    if (this.fromDevice?.className == className) {
      scene = this.fromDevice;
    }

    if (this.toDevice?.className == className) {
      scene = this.toDevice;
    }

    return scene;
  }

  getConsumer(): Consumer | undefined {
    return this.getAny("Consumer") as Consumer;
  }

  getScene(): Scene | undefined {
    return this.getAny("Scene") as Scene;
  }

  getMultiplexer(): Multiplexer | undefined {
    return this.getAny("Multiplexer") as Multiplexer;
  }
  getButtonChannel(): ButtonChannel | undefined {
    // Button channels can allso be ActorChannels -> we ned to check this before
    const buttonChannel = this.getAny("ButtonChannel") as ButtonChannel;

    return buttonChannel?.isActorChannel ? undefined : (buttonChannel as ButtonChannel);
  }

  getChannel(): Channel | undefined {
    // ButtonChannels can also be ActorChannels
    let channel = this.getAny("Channel") as Channel;
    if (!channel) {
      channel = this.getAny("ButtonChannel") as ButtonChannel;
    }
    if (channel?.isActorChannel) {
      return channel;
    }

    return undefined;
  }

  static createConnection(
    source: ConnectableObject,
    target: ConnectableObject,
    connectionType?: ConnectionType,
    askToSyncChannelType: boolean = true,
  ): CreateConnectionResult {
    let page: Page | undefined;

    let consumer: Consumer | undefined;
    // get page from Consumor or button
    if (source.isConsumer) {
      consumer = source as Consumer;
      page = consumer.parentRoom?.getOrCreatePage();
    }

    if (target.isConsumer) {
      consumer = target as Consumer;
      page = consumer.parentRoom?.getOrCreatePage();
    }

    if (source.isButtonChannel) {
      const buttonChannel = source as ButtonChannel;
      page = buttonChannel.parentRoom?.getOrCreatePage();
    }

    if (target.isButtonChannel) {
      const buttonChannel = target as ButtonChannel;
      page = buttonChannel.parentRoom?.getOrCreatePage();
      if (source.isScene) {
        connectionType = ConnectionType.Scene;
      } else {
        connectionType = ConnectionType.Button;
      }
    }

    if (target.isActorChannel) {
      if (target.parent?.className == "Button") {
        const actorChannel = target as Channel;
        const room = actorChannel.parent?.parent as Room;
        page = room?.page;
      }
      connectionType = ConnectionType.Channel;
    }

    if (!page) {
      return new CreateConnectionResult(undefined, "sys error:" + itx("Connection.noPage", " !page"));
    }

    ///////////////////////////////////////////////////////////////////////////

    if (!connectionType) {
      if (source.isDevice || source.isChannel) {
        connectionType = getTypeFromDevice(source);
      } else {
        connectionType = getTypeFromDevice(target);
      }

      if (connectionType == ConnectionType.Unknown || target.className == "Scene") {
        connectionType = getTypeFromDevice(target);
      }
      if (connectionType == ConnectionType.Unknown) {
        return new CreateConnectionResult(undefined, "sys error:" + itx("Connection.typeUnknown", " type unkown"));
      }
    } else if (connectionType == ConnectionType.FromSourceDevice) {
      connectionType = getTypeFromDevice(source);
    }

    ///////////////////////////////////////////////////////////////////////////

    //
    // adjust ChannelType if allowed by the device productData
    //

    if (target.isButtonChannel) {
      (target as ButtonChannel).autoSetChannelType(source.channelType);
    }
    if (source.isButtonChannel) {
      (source as ButtonChannel).autoSetChannelType(target.channelType);
    }

    ///////////////////////////////////////////////////////////////////////////
    let rule: "soft" | "hard" | undefined; // no itx
    if (target.isScene || source.isScene) {
      rule = "soft"; // no itx
    } else if (
      (target.isMultiplexer &&
        target.connections.size == 0 &&
        !channelTypeInfo(source.channelType).isSupply &&
        source.channelType != ChannelType.Lock) ||
      (source.isMultiplexer &&
        source.connections.size == 0 &&
        !channelTypeInfo(target.channelType).isSupply &&
        target.channelType != ChannelType.Lock)
    ) {
      rule = "soft"; // no itx
    }

    if (!AllowedConnections.isConnectionAllowed(source, target, true, rule)) {
      logger.log(`connection forbiden ${target.name}`);
      return new CreateConnectionResult(undefined, itx("Connection.connectionForbidden", "Verbindung verboten"));
    } else {
      let msg: string | undefined;

      /////////////////////////////////////////////////////////////
      // via Terminal Connection
      ////////////////////////////////////////////////////////////
      if (connectionType == ConnectionType.TerminalVia) {
        // both must be consumers
        if (!(source.isConsumer && target.isConsumer)) {
          return new CreateConnectionResult(
            undefined,
            itx(
              "Connection.terminalViaMustBeConsumers",
              "Terminal-Via Verbindungen können nur zwischen Verbrauchern hergestellt werden.",
            ),
          );
        }
        const sourceConsumer = source.isConsumer ? (source as Consumer) : undefined;
        const targetConsumer = target.isConsumer ? (target as Consumer) : undefined;
        const connection = sourceConsumer?.setTerminalViaConsumer(targetConsumer);

        if (connection) {
        }
        let msg2 = itx("Connection.T1connectedWithT2", `T1 wurde mit T2 verbunden`, {
          T1: source.tag2,
          T2: target.tag2,
        });
        if (msg) {
          msg2 += "\n" + msg;
        }
        // update relations
        sourceConsumer?.initRelations();
        targetConsumer?.initRelations();

        state.eventLogger.addEvent(EventKey.ConnectionCreated, {
          sourceId: source.id,
          targetId: target.id,
          connectionType: connectionType,
        });
        return new CreateConnectionResult(connection, msg2);
      }
      //////////////////////////////////////////////////////////////////////
      // other connection Types
      //////////////////////////////////////////////////////////////////////

      const connection = new Connection({}, page);
      connection.type = connectionType;
      connection.fromDevice = source;
      connection.toDevice = target;
      page.connections.add(connection);

      connection.specifyGenericConnection();

      // to set selectedForAutoconnection
      if (source.parent?.className == "Actor") {
        (source as Channel).selectedForAutoconect = false;
      } else if (target.parent?.className == "Actor") {
        (target as Channel).selectedForAutoconect = false;
      }

      /// multiplexer
      if (connection.fromDevice.className == "Multiplexer") {
        (connection.fromDevice as Multiplexer).changeChangableChannelType(connection.toDevice);
      } else if (connection.toDevice.className == "Multiplexer") {
        (connection.toDevice as Multiplexer).changeChangableChannelType(connection.fromDevice);
      }

      /////////////////////////////////////////////////////////////////////////////////////
      //
      // Remove old Channel-Connection
      //
      //
      if (connectionType == ConnectionType.Channel) {
        if (consumer) {
          const oldConnection = consumer.connections.findFirst((c) => c.type == ConnectionType.Channel);
          const oldChannel = oldConnection?.getChannel();

          if (oldChannel) {
            const newChannel = connection.getChannel();
            if (newChannel == oldChannel) {
              return new CreateConnectionResult(
                undefined,
                itx("Connection.connectionAlreadyExists", "Verbindung besteht bereits"),
              );
            }
            if (oldConnection) {
              oldConnection?.delete();
            }
            msg = itx("Connection.connectionMovedToAnotherChannel", "Verbinung wurde auf einen anderen Kanal verlegt");
          }
        }
      }

      /////////////////////////////////////////////////////////////////////////////////////

      connection.updateEndPoints();
      connection.addToPage();
      connection.addToConnectableObjects();
      connection.updateConnectedPartners();

      if (
        !AllowedConnections.isConnectionAllowed(
          source,
          target,
          false,
          "hard", // no itx
        )
      ) {
        connection.forbidden = true;
      }

      let t1 = source.isScene ? source.name : source.tag2;
      let t2 = target.isScene ? target.name : target.tag2;

      t1 = itx("components." + source.className) + ": " + t1;
      t2 = itx("components." + target.className) + ": " + t2;

      let msg2 = itx("Connection.T1connectedWithT2", `T1 wurde mit T2 verbunden`, {
        T1: t1,
        T2: t2,
      });

      if (msg) {
        msg2 += "\n" + msg;
      }
      source.raiseOnChange("childConnections");
      target.raiseOnChange("childConnections");

      state.eventLogger.addEvent(EventKey.ConnectionCreated, {
        sourceId: source.id,
        targetId: target.id,
        connectionType: connectionType,
      });

      if (connectionType == ConnectionType.Channel) {
        askToSyncConsumerChannelType(connection.getChannel(), connection.getConsumer(), askToSyncChannelType);
      }

      return new CreateConnectionResult(connection, msg2);
    }
  }

  static getConnectionType(source: ConnectableObject, target: ConnectableObject): ConnectionType {
    let connectionType = ConnectionType.Unknown;
    if (source.className == "Consumer" || target.className == "Consumer") {
      connectionType = ConnectionType.FromSourceDevice;
    }
    if (source.className == "ButtonChannel" || target.className == "ButtonChannel") {
      connectionType = ConnectionType.Button;
    }
    if (source.className == "Channel" || target.className == "Channel") {
      connectionType = ConnectionType.Channel;
    }
    if (source.className == "Scene" || target.className == "Scene") {
      connectionType = ConnectionType.Scene;
    }

    return connectionType;
  }

  checkOwnErrors() {
    this.checkForbidden();
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);
    const fromDevice = this.fromDevice;
    const toDevice = this.toDevice;

    if ((toDevice?.isScene && fromDevice?.isConsumer) || (fromDevice?.isScene && toDevice?.isConsumer)) {
      if (this.type != ConnectionType.Scene) {
        this.logConsistencyError(
          `Connection type is not ConnectionType.Scene:  ${this.fromDevice?.fullTag || "--"} <-> ${this.toDevice?.fullTag || "--"}`,
        );
        this.type = ConnectionType.Scene;
      }
    }

    if (this.toDevice == undefined || this.fromDevice == undefined || this.toDevice == this.fromDevice) {
      this.logConsistencyError(
        `Connection is inconsistent:  ${this.fromDevice?.fullTag || "--"} <-> ${this.toDevice?.fullTag || "--"}`,
      );
      this.delete();
    }
  }

  /** Marks logical contact interlocks; contact wiring and actor-channel wiring stay solid. */
  get isLockConnection(): boolean {
    const source = this.fromDevice;
    const target = this.toDevice;
    if (!source || !target || source.isActorChannel || target.isActorChannel) return false;
    const sourceIsContact = source.channelType === ChannelType.Lock;
    const targetIsContact = target.channelType === ChannelType.Lock;
    return sourceIsContact !== targetIsContact;
  }
}

export class CreateConnectionResult {
  constructor(connection?: Connection | undefined, msg?: string | undefined) {
    this.connection = connection;
    this.msg = msg;
  }
  connection: Connection | undefined;
  msg: string | undefined;
}

//
// after connecting an actor channel with a consumer of a different channelType, offer to
// adopt the actor channel's channelType on the consumer instead of silently keeping the mismatch
// callers that don't want the prompt (e.g. bulk imports) pass ask=false
//
export async function askToSyncConsumerChannelType(
  channel: Channel | undefined,
  consumer: Consumer | undefined,
  ask: boolean = true,
) {
  if (!ask || !channel || !consumer || electricalChannelType(channel.channelType) == electricalChannelType(consumer.channelType)) {
    return;
  }

  // A LAN patch-panel channel also serves double LAN sockets. Lan2 describes
  // their port count, so connecting the channel must not overwrite that choice.
  if (channelTypeInfo(channel.channelType).isLan && channelTypeInfo(consumer.channelType).isLan) return;

  // Jalousie-Aktorkanäle unterstützen auch Rollos; der Consumer behält seinen Typ.
  if (
    (channel.channelType === ChannelType.Jalousie && consumer.channelType === ChannelType.Blind) ||
    (channel.channelType === ChannelType.Blind && consumer.channelType === ChannelType.Jalousie)
  ) return;

  // the Lock/Rollo interlock connection intentionally combines two different channelTypes
  if (
    (channel.channelType == ChannelType.Lock && channelTypeInfo(consumer.channelType)?.isRollo) ||
    (consumer.channelType == ChannelType.Lock && channelTypeInfo(channel.channelType)?.isRollo)
  ) {
    return;
  }

  const ok = await estadaConfirm(
    itx("consumer.changeChannelType", "Channel Type ändern"),
    itx(
      "consumer.changeChannelTypeFromT1ToT2",
      `willst du den ChannelType von ${consumer.channelType} auf ${channel.channelType} ändern?`,
      { T1: itx(`channelType.${consumer.channelType}`), T2: itx(`channelType.${channel.channelType}`) },
    ),
  );
  if (ok) {
    consumer.changeChannelType(channel.channelType);
  }
}

export function highlightConnections(
  hover: boolean,
  page: Page,
  obj: ConnectableObject | Device,
  print?: PagePrintInfo,
) {
  if (!print) {
    if (!page.connectionManipulationActive) {
      if (obj.getIsDevice()) {
        const device = obj as Device;
        device.channels.forEach((channel) => {
          channel.highLightConnections(hover, undefined, device.id);
        });
      } else {
        obj.highLightConnections(hover, undefined);
      }
    }
  }
}

export function dehighlightConnections(
  hover: boolean,
  page: Page,
  obj: ConnectableObject | Device,
  print?: PagePrintInfo,
) {
  if (!print) {
    if (!page.connectionManipulationActive) {
      if (obj.getIsDevice()) {
        const device = obj as Device;

        device.channels.forEach((channel) => {
          channel.dehighLightConnections(hover);
        });
      } else {
        obj.dehighLightConnections(hover);
      }
    }
  }
}
